import { useMemo, useState } from 'react';

interface EmailPreviewProps {
  subject: string;
  body: string;
  placeholders: { key: string; description: string }[];
}

const SAMPLE_VALUES: Record<string, string> = {
  '{{nome}}': 'Mario',
  '{{cognome}}': 'Rossi',
  '{{email}}': 'mario.rossi@example.com',
  '{{link_questionario}}': 'https://app.klaro7.com/questionnaire/a1b2c3d4-e5f6-7890-abcd-ef1234567890',
};

type PreviewMode = 'desktop' | 'mobile';

function substitutePlaceholders(text: string): string {
  return text.replace(/\{\{[^}]+\}\}/g, (match) => {
    const sample = SAMPLE_VALUES[match];
    if (sample) return sample;
    return `<span style="background:#fef08a;padding:0 4px;border-radius:3px">${match}</span>`;
  });
}

export function EmailPreview({ subject, body }: EmailPreviewProps) {
  const [mode, setMode] = useState<PreviewMode>('desktop');

  const renderedSubject = useMemo(() => substitutePlaceholders(subject), [subject]);
  const renderedBody = useMemo(() => substitutePlaceholders(body), [body]);

  return (
    <div className="space-y-3">
      {/* Desktop / Mobile toggle */}
      <div className="flex items-center gap-1 rounded-full bg-neutral-100 p-1 w-fit">
        <button
          type="button"
          onClick={() => setMode('desktop')}
          className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
            mode === 'desktop'
              ? 'bg-brand-teal text-white shadow-sm'
              : 'text-neutral-600 hover:text-neutral-900'
          }`}
        >
          Desktop
        </button>
        <button
          type="button"
          onClick={() => setMode('mobile')}
          className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
            mode === 'mobile'
              ? 'bg-brand-teal text-white shadow-sm'
              : 'text-neutral-600 hover:text-neutral-900'
          }`}
        >
          Mobile
        </button>
      </div>

      {/* Preview container */}
      <div className="flex justify-center rounded-xl border border-neutral-200 bg-neutral-50 p-6">
        <div
          className={`w-full transition-all duration-300 ease-in-out ${
            mode === 'desktop' ? 'max-w-2xl' : 'max-w-sm'
          }`}
          style={mode === 'mobile' ? { width: 375 } : undefined}
        >
          <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm">
            {/* Email header */}
            <div className="border-b border-neutral-100 bg-neutral-50 px-5 py-3.5 space-y-1">
              <p className="text-xs text-neutral-500">
                <span className="font-medium text-neutral-600">Da:</span>{' '}
                Klaro7 &lt;noreply@klaro7.com&gt;
              </p>
              <p className="text-xs text-neutral-500">
                <span className="font-medium text-neutral-600">Oggetto:</span>{' '}
                <span dangerouslySetInnerHTML={{ __html: renderedSubject }} />
              </p>
            </div>

            {/* Email body */}
            <div
              className="max-h-[480px] overflow-y-auto p-5"
              style={{
                fontFamily: 'sans-serif',
                lineHeight: 1.6,
                color: '#333',
              }}
              dangerouslySetInnerHTML={{ __html: renderedBody }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
